import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import Tooltip from "@mui/material/Tooltip";
import heic2any from "heic2any";
import { useDatabase } from "./DatabaseContext";
import { uploadFileWithMetadata } from "../../api/companyFileCategories";
import { uploadFileToS3Complete, getSignedDownloadUrl } from "../../api/s3";
import LoadingSpinner from "../ui/LoadingSpinner";
import TextInput from "../ui/TextInput";
import { ReactComponent as DeleteBinSvg } from "../../assets/images/delete-bin-line.svg";
import { ReactComponent as DownloadIcon } from "../../assets/images/download-line.svg";
import { ReactComponent as SettingsIcon } from "../../assets/images/settings-4-line.svg";
import { ReactComponent as TimeIcon } from "../../assets/images/time-line.svg";
import { ReactComponent as RefreshIcon } from "../../assets/images/refresh-line.svg";
import { ReactComponent as FilterIcon } from "../../assets/images/filter-line.svg";
import { ReactComponent as SortAscIcon } from "../../assets/images/sort-asc.svg";
import { ReactComponent as SortDescIcon } from "../../assets/images/sort-desc.svg";
import { ReactComponent as SortNumberAscIcon } from "../../assets/images/sort-number-asc.svg";
import { ReactComponent as SortNumberDescIcon } from "../../assets/images/sort-number-desc.svg";
import { ReactComponent as MenuSearchIcon } from "../../assets/images/menu-search-line.svg";
import { ReactComponent as CloseCircleIcon } from "../../assets/images/close-circle-line.svg";
import { ReactComponent as EyeIcon } from "../../assets/images/eye-line.svg";
import { ReactComponent as InputMethodIcon } from "../../assets/images/input-method-line.svg";
import { ReactComponent as TBoxIcon } from "../../assets/images/t-box-line.svg";
import Icon from "../common/Icon";
import { acceptedFileTypes, MAX_FILE_SIZE, MAX_FILES_UPLOAD } from "./constants";
import { getAdminInfo } from "../../api";
import { useNotification } from "../../context/NotificationContext";
import { User } from "../../types/types";
import FileMetadataModal from "../modals/FileMetadataModal";
import EditFileMetadataModal from "../modals/EditFileMetadataModal";
import DatabaseFilterModal, { DatabaseFilters, DatabaseSort } from "../modals/DatabaseFilterModal";
import ChunksModal from "../modals/ChunksModal";
import FilePreviewModal from "../modals/FilePreviewModal";
import DeleteModal from "../modals/DeleteModal";
import { validateFilesSignatures } from "../../utils/fileValidation";

const getFileTypeBadge = (fileName: string) => {
    const lower = fileName.toLowerCase();
    let label = "FILE";
    let tone = "fi-file";

    if (lower.endsWith(".pdf")) {
        label = "PDF";
        tone = "fi-pdf";
    } else if (lower.endsWith(".doc") || lower.endsWith(".docx")) {
        label = "DOC";
        tone = "fi-doc";
    } else if (lower.endsWith(".txt")) {
        label = "TXT";
        tone = "fi-txt";
    } else if (
        lower.endsWith(".jpg") ||
        lower.endsWith(".jpeg") ||
        lower.endsWith(".png") ||
        lower.endsWith(".webp") ||
        lower.endsWith(".heic")
    ) {
        label = lower.endsWith(".png")
            ? "PNG"
            : lower.endsWith(".webp")
              ? "WEB"
              : lower.endsWith(".heic")
                ? "HEIC"
                : "JPG";
        tone = "fi-jpg";
    } else if (lower.endsWith(".csv")) {
        label = "CSV";
        tone = "fi-xls";
    } else if (lower.endsWith(".xls") || lower.endsWith(".xlsx")) {
        label = "XLS";
        tone = "fi-xls";
    }

    return <span className={`file-ico ${tone}`}>{label}</span>;
};

const renderMatchTypeIcons = (matchType: string | undefined, t: any) => {
    if (!matchType) return null;

    const hasVector = matchType === "vector_only" || matchType === "both";
    const hasText = matchType === "text_only" || matchType === "both";

    return (
        <div className="database-match-type-icons">
            {hasVector && (
                <Tooltip
                    title={t("vectorMatchTooltip") || "AI vector similarity - matches based on context and meaning"}
                    arrow
                >
                    <InputMethodIcon className="database-match-type-icon vector-match-icon" />
                </Tooltip>
            )}
            {hasText && (
                <Tooltip title={t("textMatchTooltip") || "Exact text match - all words in query were found"} arrow>
                    <TBoxIcon className="database-match-type-icon text-match-icon" />
                </Tooltip>
            )}
        </div>
    );
};

/**
 * - Remove accents and diacritics
 * - Replace any character that is not a letter, number, underscore, or hyphen with an underscore
 * - Preserve the original file extension (e.g., ".pdf", ".docx")
 * - Truncate the sanitized name part to a maximum of 100 characters
 */
const sanitizeFilename = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf(".");

    if (dotIndex === -1) {
        // No extension
        return fileName
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-zA-Z0-9_-]/g, "_")
            .slice(0, 100);
    }

    // Removes extension
    const name = fileName.substring(0, dotIndex);

    const safeName = name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9_-]/g, "_")
        .slice(0, 100);

    return safeName;
};

const DatabaseContent: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const {
        selectedFolderId,
        files,
        handleDeleteFile,
        refreshFiles,
        isRefreshing,
        isDeleting,
        isFileLimitReached,
        triggerQueueRefresh,
        pagination,
        updateQueryParams,
        searchParams,
    } = useDatabase();

    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [searchDebounced, setSearchDebounced] = useState("");

    // Filter modal state
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Snippet modal state
    const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
    const [selectedSnippet, setSelectedSnippet] = useState<{
        fileName: string;
        snippet: string | undefined;
        description?: string;
        title?: string;
        author?: string;
    }>({ fileName: "", snippet: undefined });

    // Metadata modal states
    const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);

    // Edit metadata modal states
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingFile, setEditingFile] = useState<{ id: string; name: string; metadata: any } | null>(null);

    // Preview modal state
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type?: string } | null>(null);

    // Delete modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<{ id: string; name: string } | null>(null);

    const token = localStorage.getItem("token");

    const { showNotification } = useNotification();

    const [user, setUser] = useState<User | null>(null);

    // Debounce search query (increased to 1000ms to ensure user finished typing)
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(searchQuery);
        }, 1000);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Update URL when search changes
    useEffect(() => {
        const currentUrlSearch = searchParams.get("vectorQuery") || "";
        if (searchDebounced !== currentUrlSearch) {
            updateQueryParams({
                vectorQuery: searchDebounced || undefined,
                page: "1", // Reset to page 1 on search
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDebounced, updateQueryParams]);

    // Sync search input with URL on mount
    useEffect(() => {
        const urlSearch = searchParams.get("vectorQuery") || "";
        setSearchQuery(urlSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Fetch user info
    const fetchUserInfo = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }

            const userData = await getAdminInfo({ token });
            setUser(userData);
            return userData.cmpany?.stenomaticClientId;
        } catch (error) {
            console.error("Error fetching user info:", error);
            showNotification({
                message: t("errorFetchingCompanyInfo"),
                type: "error",
            });
            return null;
        }
    }, [showNotification, t]);

    useEffect(() => {
        fetchUserInfo();
    }, [fetchUserInfo]);

    const {
        getRootProps,
        getInputProps,
        open: openFileDialog,
        isDragActive,
    } = useDropzone({
        onDrop: (acceptedFiles) => handleFilesSelected(acceptedFiles),
        accept: acceptedFileTypes,
        maxSize: MAX_FILE_SIZE,
        maxFiles: MAX_FILES_UPLOAD,
        multiple: true,
        disabled: isFileLimitReached || isUploading || isRefreshing,
        noClick: true, // prevent auto open
        onDropRejected: (rejectedFiles) => {
            // Check for different error types and set appropriate messages
            if (rejectedFiles.some((file) => file.errors.some((error) => error.code === "file-too-large"))) {
                setErrorMessage(t("fileTooBig", { size: (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0) }));
            } else if (rejectedFiles.some((file) => file.errors.some((error) => error.code === "file-invalid-type"))) {
                const acceptedExtensions = Object.values(acceptedFileTypes).flat().join(", ");
                setErrorMessage(t("invalidFileType", { types: acceptedExtensions }));
            } else if (rejectedFiles.some((file) => file.errors.some((error) => error.code === "too-many-files"))) {
                setErrorMessage(t("tooManyFiles", { max: MAX_FILES_UPLOAD }));
            } else {
                setErrorMessage(t("uploadFailed"));
            }
            // Clear error after 5 seconds
            setTimeout(() => setErrorMessage(null), 5000);
        },
    });

    // Manual refresh function
    const handleManualRefresh = useCallback(async () => {
        if (isRefreshing) return; // Prevent clicks during any refresh

        try {
            await refreshFiles();
            showNotification({
                message: t("filesRefreshed") || "Files refreshed successfully",
                type: "success",
            });
        } catch (error) {
            console.error("Error refreshing files:", error);
            showNotification({
                message: t("errorRefreshingFiles") || "Error refreshing files",
                type: "error",
            });
        }
    }, [isRefreshing, refreshFiles, showNotification, t]);

    const handleFilesSelected = async (filesInput: File[]) => {
        if (!token || !user) return;

        // Clear any previous error messages
        setErrorMessage(null);

        // Validate file signatures before proceeding
        try {
            const { validFiles, invalidFiles } = await validateFilesSignatures(filesInput);

            if (invalidFiles.length > 0) {
                // Map error codes to translation keys
                const errorCodeMap: Record<string, string> = {
                    FILE_SIGNATURE_MISMATCH_PDF: "fileSignatureMismatchPDF",
                    FILE_SIGNATURE_MISMATCH_DOCX: "fileSignatureMismatchDOCX",
                    FILE_SIGNATURE_MISMATCH_JPEG: "fileSignatureMismatchJPEG",
                    FILE_SIGNATURE_MISMATCH_PNG: "fileSignatureMismatchPNG",
                    FILE_SIGNATURE_MISMATCH_WEBP: "fileSignatureMismatchWEBP",
                    FILE_SIGNATURE_MISMATCH_XLS: "fileSignatureMismatchXLS",
                    FILE_SIGNATURE_MISMATCH_XLSX: "fileSignatureMismatchXLSX",
                    FILE_SIGNATURE_MISMATCH_HEIC: "fileSignatureMismatchHEIC",
                    FILE_SIGNATURE_MISMATCH_CSV: "fileSignatureMismatchCSV",
                    FILE_SIGNATURE_MISMATCH_TXT: "fileSignatureMismatchTXT",
                };

                // Translate each error message
                const errorMessages = invalidFiles
                    .map(({ file, errorCode }) => {
                        const translationKey = errorCodeMap[errorCode] || "fileSignatureMismatch";
                        return t(translationKey, { fileName: file.name }) || `${file.name}: Invalid file signature`;
                    })
                    .join("\n");

                const errorMsg =
                    t("fileSignatureMismatch", {
                        count: invalidFiles.length,
                        details: errorMessages,
                    }) || `${invalidFiles.length} file(s) have extension/content mismatch:\n${errorMessages}`;

                // Show notification only (no need for duplicate error message in dropzone)
                showNotification({
                    message: errorMsg,
                    type: "error",
                });

                // If all files are invalid, don't proceed
                if (validFiles.length === 0) {
                    return;
                }

                // If some files are valid, proceed with valid ones only
                if (validFiles.length > 0) {
                    showNotification({
                        message:
                            t("someFilesInvalidSignature", {
                                valid: validFiles.length,
                                invalid: invalidFiles.length,
                            }) ||
                            `${validFiles.length} file(s) are valid. ${invalidFiles.length} file(s) were rejected due to extension/content mismatch.`,
                        type: "warning",
                    });
                }
            }

            // Store valid files and open metadata modal
            setPendingFiles(validFiles.length > 0 ? validFiles : filesInput);
            setIsMetadataModalOpen(true);
        } catch (error) {
            console.error("Error validating file signatures:", error);
            // If validation fails, proceed with original files (backend will catch it)
            setPendingFiles(filesInput);
            setIsMetadataModalOpen(true);
        }
    };

    const handleUploadWithMetadata = async (filesWithMetadata: any[]) => {
        if (!token || !user) return;

        setIsUploading(true);
        setErrorMessage(null);

        const uploadResults: { success: number; failed: number; failedFiles: string[] } = {
            success: 0,
            failed: 0,
            failedFiles: [],
        };

        try {
            for (const fileWithMetadata of filesWithMetadata) {
                // Safely destructure with fallback for missing metadata
                const { file, metadata } = fileWithMetadata || {};

                // Skip if file is missing
                if (!file) {
                    console.warn("Skipping upload: file is missing");
                    uploadResults.failed++;
                    continue;
                }

                // Skip if metadata is missing
                if (!metadata) {
                    console.warn("Skipping upload: metadata is missing for file", file.name);
                    uploadResults.failed++;
                    uploadResults.failedFiles.push(file.name);
                    continue;
                }
                try {
                    // Check if file is HEIC and needs conversion
                    const isHEIC = file.name.toLowerCase().endsWith(".heic");
                    let temporaryProcessingUrl: string | undefined = undefined;

                    // Upload original file to S3
                    const uploadedUrl = await uploadFileToS3Complete(
                        {
                            file,
                            bucket: "rag-knowledge-database",
                            isPublic: false, // Private files for knowledge database
                            isFolder: true, // Use folder structure
                        },
                        token,
                    );

                    // If HEIC, convert to JPEG and upload temporary processing file
                    if (isHEIC) {
                        try {
                            // Convert HEIC to JPEG
                            const convertedBlob = await heic2any({
                                blob: file,
                                toType: "image/jpeg",
                                quality: 0.9,
                            });

                            // heic2any might return Blob or Blob[], ensure we have a single Blob
                            const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

                            // Create a File object from the Blob with .jpg extension
                            const jpegFileName = file.name.replace(/\.(heic)$/i, ".jpg");
                            const jpegFile = new File([jpegBlob], jpegFileName, { type: "image/jpeg" });

                            // Upload JPEG to S3 as temporary processing file
                            temporaryProcessingUrl = await uploadFileToS3Complete(
                                {
                                    file: jpegFile,
                                    bucket: "rag-knowledge-database",
                                    isPublic: false,
                                    isFolder: true,
                                },
                                token,
                            );

                            console.log("HEIC converted and uploaded successfully:", temporaryProcessingUrl);
                        } catch (conversionError) {
                            console.error("Failed to convert HEIC to JPEG:", conversionError);
                            throw new Error(
                                t("heicConversionFailed") ||
                                    "Failed to convert HEIC image. Please try a different format.",
                            );
                        }
                    }

                    const sanitizedFileName = sanitizeFilename(file.name);

                    // Upload with metadata to vector store
                    const fileData: any = {
                        documentUrl: uploadedUrl,
                        documentName: sanitizedFileName,
                        companyFileCategoryIds: metadata.companyFileCategoryIds,
                        title: metadata.title,
                        description: metadata.description,
                        author: metadata.author,
                        date: metadata.date,
                        type: metadata.type,
                        language: metadata.language,
                        companyRagFolderId: metadata.companyRagFolderId,
                        rightsOwner: metadata.rightsOwner,
                        creditLine: metadata.creditLine,
                        companyFileTagIds: metadata.companyFileTagIds,
                        companyUsageTypeMetadataIds: metadata.companyUsageTypeMetadataIds,
                        companyRightsTermMetadataIds: metadata.companyRightsTermMetadataIds,
                        companyTerritoryMetadataIds: metadata.companyTerritoryMetadataIds,
                        companyAllowedMediaMetadataIds: metadata.companyAllowedMediaMetadataIds,
                    };

                    // Add temporaryProcessingUrl if it exists (for HEIC files)
                    if (temporaryProcessingUrl) {
                        fileData.temporaryProcessingUrl = temporaryProcessingUrl;
                    }

                    await uploadFileWithMetadata({
                        token,
                        fileData,
                    });

                    uploadResults.success++;
                } catch (error: unknown) {
                    console.error("Failed to upload file:", error);
                    uploadResults.failed++;

                    // Try to extract and translate error message from backend
                    let errorMsg = file.name;
                    try {
                        // Type guard for error with details property (from fetchAPI)
                        interface ApiError extends Error {
                            statusCode?: number;
                            details?: {
                                errorCode?: string;
                                fileName?: string;
                                message?: string;
                            };
                            body?: {
                                errorCode?: string;
                                fileName?: string;
                                message?: string;
                            };
                        }

                        const isApiError = (err: unknown): err is ApiError => {
                            return err instanceof Error && ("details" in err || "body" in err);
                        };

                        if (isApiError(error)) {
                            // Check error.details (from fetchAPI) or error.body (direct response)
                            const errorResponse = error.details || error.body;
                            const errorCode = errorResponse?.errorCode;
                            const fileName = errorResponse?.fileName || file.name;

                            // Map error codes to translation keys
                            const errorCodeMap: Record<string, string> = {
                                FILE_SIGNATURE_MISMATCH_PDF: "fileSignatureMismatchPDF",
                                FILE_SIGNATURE_MISMATCH_DOCX: "fileSignatureMismatchDOCX",
                                FILE_SIGNATURE_MISMATCH_JPEG: "fileSignatureMismatchJPEG",
                                FILE_SIGNATURE_MISMATCH_PNG: "fileSignatureMismatchPNG",
                                FILE_SIGNATURE_MISMATCH_WEBP: "fileSignatureMismatchWEBP",
                                FILE_SIGNATURE_MISMATCH_XLS: "fileSignatureMismatchXLS",
                                FILE_SIGNATURE_MISMATCH_XLSX: "fileSignatureMismatchXLSX",
                                FILE_SIGNATURE_MISMATCH_HEIC: "fileSignatureMismatchHEIC",
                                FILE_SIGNATURE_MISMATCH_CSV: "fileSignatureMismatchCSV",
                                FILE_SIGNATURE_MISMATCH_TXT: "fileSignatureMismatchTXT",
                            };

                            if (errorCode && errorCode in errorCodeMap) {
                                const translationKey = errorCodeMap[errorCode];
                                errorMsg = t(translationKey, { fileName }) || errorResponse.message || file.name;
                            } else if (errorResponse?.message) {
                                // Fallback to backend message if no translation key found
                                errorMsg = errorResponse.message;
                            }
                        }
                    } catch (translationError) {
                        // If translation fails, just use file name
                        console.warn("Error translating error message:", translationError);
                    }

                    uploadResults.failedFiles.push(errorMsg);
                }
            }

            // Trigger queue status refresh
            triggerQueueRefresh();

            // Show appropriate notification based on results
            if (uploadResults.failed === 0) {
                // All files succeeded
                setErrorMessage(null); // Clear any error messages
                showNotification({
                    message: t("filesAddedToQueue") || "Files added successfully and will be processed soon",
                    type: "success",
                });
            } else if (uploadResults.success === 0) {
                // All files failed
                const errorMsg =
                    uploadResults.failedFiles.length > 0
                        ? t("uploadFailedForFiles", { files: uploadResults.failedFiles.join(", ") }) ||
                          `Upload failed for: ${uploadResults.failedFiles.join(", ")}`
                        : t("uploadFailed");
                // Show notification only (no need for duplicate error message in dropzone)
                showNotification({
                    message: errorMsg,
                    type: "error",
                });
            } else {
                // Mixed results - some succeeded, some failed
                const errorMsg =
                    t("someFilesUploadFailed", {
                        success: uploadResults.success,
                        failed: uploadResults.failed,
                        files: uploadResults.failedFiles.join(", "),
                    }) ||
                    `${uploadResults.success} file(s) uploaded successfully, but ${uploadResults.failed} file(s) failed: ${uploadResults.failedFiles.join(", ")}`;
                // Show notification only (no need for duplicate error message in dropzone)
                showNotification({
                    message: errorMsg,
                    type: "warning",
                });
            }
        } catch (error) {
            console.error("Upload failed:", error);
            // Show notification only (no need for duplicate error message in dropzone)
            showNotification({
                message: t("uploadFailed"),
                type: "error",
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleEditMetadata = (file: any) => {
        console.log("file", file);
        // Set the file to edit with its current metadata
        setEditingFile({
            id: file.id,
            name: file.name,
            metadata: {
                title: file.title || "",
                description: file.description || "",
                author: file.author || "",
                date: file.date ? (file.date.includes("T") ? file.date.split("T")[0] : file.date) : "",
                type: file.type || "",
                language: file.language || "",
                // Use categories field which has full objects, or fall back to companyFileCategoryIds
                companyFileCategoryIds:
                    file.categories?.map((cat: any) => cat.id) ||
                    file.companyFileCategoryIds?.map((cat: any) => cat.id) ||
                    [],
                companyRagFolderId: file.companyRagFolderId || "",
                companyFileTagIds:
                    file.tags?.map((tag: any) => tag.id) || file.companyFileTagIds?.map((tag: any) => tag.id) || [],
                rightsOwner: file.rightsOwner || "",
                creditLine: file.creditLine || "",
                companyUsageTypeMetadataIds:
                    file.usageTypes?.map((type: any) => type.id) ||
                    file.companyUsageTypeMetadataIds?.map((type: any) => type.id) ||
                    [],
                companyRightsTermMetadataIds:
                    file.rightsTerms?.map((term: any) => term.id) ||
                    file.companyRightsTermMetadataIds?.map((term: any) => term.id) ||
                    [],
                companyTerritoryMetadataIds:
                    file.territories?.map((territory: any) => territory.id) ||
                    file.companyTerritoryMetadataIds?.map((territory: any) => territory.id) ||
                    [],
                companyAllowedMediaMetadataIds:
                    file.allowedMedia?.map((media: any) => media.id) ||
                    file.companyAllowedMediaMetadataIds?.map((media: any) => media.id) ||
                    [],
            },
        });
        setIsEditModalOpen(true);
    };

    const handleMetadataUpdate = (fileId: string, metadata: any) => {
        // Update the editingFile state immediately for real-time updates
        if (editingFile && editingFile.id === fileId) {
            setEditingFile((prev) =>
                prev
                    ? {
                          ...prev,
                          metadata: {
                              ...prev.metadata,
                              ...metadata,
                          },
                      }
                    : null,
            );
        }
    };

    const handleSaveMetadata = async (fileId: string, metadata: any) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                showNotification({
                    message: "Authentication token not found",
                    type: "error",
                });
                setLoading(false);
                return;
            }

            // Import the API function
            const { updateCompanyFileMetadata } = await import("../../api/companyFileCategories");

            // Prepare metadata for API call
            const updateData: any = {};
            Object.keys(metadata).forEach((key) => {
                const value = metadata[key];
                if (value !== undefined) {
                    if (key === "companyFileCategoryIds") {
                        updateData[key] = value;
                    } else if (key === "companyRagFolderId") {
                        // Send the folder ID, or null if empty string (to clear folder)
                        updateData[key] = value === "" ? null : value;
                    } else {
                        updateData[key] = value === "" ? null : value;
                    }
                }
            });

            await updateCompanyFileMetadata({
                token,
                fileId,
                metadata: updateData,
            });

            // Refresh files and show success
            refreshFiles();
            showNotification({
                message: t("metadataUpdatedSuccessfully") || "Metadata updated successfully",
                type: "success",
            });

            // Close modal
            setIsEditModalOpen(false);
            setLoading(false);
            setEditingFile(null);
        } catch (err: any) {
            setLoading(false);
            showNotification({
                message: err.message || "Failed to update metadata",
                type: "error",
            });
        }
    };

    const handleFileDownload = async (fileUrl: string, fileName: string) => {
        if (!fileUrl || !token) return;

        try {
            // For private files, get a signed download URL
            const downloadResponse = await getSignedDownloadUrl({ fileUrl }, token);
            const link = document.createElement("a");
            link.href = downloadResponse.signedUrl;
            link.download = fileName || "download";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error downloading file:", error);
            showNotification({
                message: t("errorDownloadingFile") || "Error downloading file",
                type: "error",
            });
        }
    };

    // Get current filters and sort from URL
    const currentFilters: DatabaseFilters = useMemo(() => {
        return {
            name: searchParams.get("name") || undefined,
            title: searchParams.get("title") || undefined,
            description: searchParams.get("description") || undefined,
            author: searchParams.get("author") || undefined,
            type: searchParams.get("type") || undefined,
            language: searchParams.get("language") || undefined,
            categoryId: searchParams.get("categoryId") || undefined,
            tagId: searchParams.get("tagId") || undefined,
            rightsOwner: searchParams.get("rightsOwner") || undefined,
            creditLine: searchParams.get("creditLine") || undefined,
            usageTypeId: searchParams.get("usageTypeId") || undefined,
            rightsTermId: searchParams.get("rightsTermId") || undefined,
            territoryId: searchParams.get("territoryId") || undefined,
            allowedMediaId: searchParams.get("allowedMediaId") || undefined,
            createdAtFrom: searchParams.get("createdAtFrom") || undefined,
            createdAtTo: searchParams.get("createdAtTo") || undefined,
            dateFrom: searchParams.get("dateFrom") || undefined,
            dateTo: searchParams.get("dateTo") || undefined,
        };
    }, [searchParams]);

    const currentSort: DatabaseSort = useMemo(() => {
        return {
            sortBy: searchParams.get("sortBy") || "createdAt",
            sortOrder: (searchParams.get("sortOrder") || "desc") as "asc" | "desc",
        };
    }, [searchParams]);

    const handleApplyFilters = (filters: DatabaseFilters) => {
        // Build updates object, explicitly setting undefined for filter fields that should be removed
        const updates: Record<string, string | undefined> = {
            // Explicitly handle all filter fields - set to undefined if not present to remove from URL
            name: filters.name || undefined,
            title: filters.title || undefined,
            description: filters.description || undefined,
            author: filters.author || undefined,
            type: filters.type || undefined,
            language: filters.language || undefined,
            categoryId: filters.categoryId || undefined,
            tagId: filters.tagId || undefined,
            rightsOwner: filters.rightsOwner || undefined,
            creditLine: filters.creditLine || undefined,
            usageTypeId: filters.usageTypeId || undefined,
            rightsTermId: filters.rightsTermId || undefined,
            territoryId: filters.territoryId || undefined,
            allowedMediaId: filters.allowedMediaId || undefined,
            createdAtFrom: filters.createdAtFrom || undefined,
            createdAtTo: filters.createdAtTo || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
            page: "1", // Reset to page 1 when filters change
        };
        updateQueryParams(updates);
    };

    // Handle sort change
    const handleSortChange = (sortBy: string, sortOrder: "asc" | "desc") => {
        updateQueryParams({ sortBy, sortOrder, page: "1" });
    };

    // Cycle through sort options for a given field
    const handleSortClick = (field: string) => {
        const isSameField = currentSort.sortBy === field;
        if (isSameField) {
            // Toggle order
            const newOrder = currentSort.sortOrder === "asc" ? "desc" : "asc";
            handleSortChange(field, newOrder);
        } else {
            // New field, default to descending
            handleSortChange(field, "desc");
        }
    };

    const handleShowSnippet = (file: any) => {
        setSelectedSnippet({
            fileName: file._searchMatch?.fileName || file.name || "Unnamed File",
            snippet: file._searchMatch?.snippet,
            title: file.title,
            description: file.description,
            author: file.author,
        });
        setIsSnippetModalOpen(true);
    };

    const handlePreviewFile = (file: any) => {
        setPreviewFile({
            url: file.url,
            name: file.name || "Unnamed File",
            type: file.type,
        });
        setIsPreviewModalOpen(true);
    };

    const handleOpenDeleteModal = (file: any) => {
        setFileToDelete({
            id: file.id,
            name: file.name || "Unnamed File",
        });
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!fileToDelete) return;

        try {
            await handleDeleteFile(fileToDelete.id);
            showNotification({
                message: t("fileDeletedSuccessfully") || "File deleted successfully",
                type: "success",
            });
        } catch (error) {
            console.error("Error deleting file:", error);
            showNotification({
                message: t("errorDeletingFile") || "Error deleting file",
                type: "error",
            });
        }
    };

    // Pagination
    const handlePageChange = (newPage: number) => {
        updateQueryParams({ page: String(newPage) });
        // Scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const visiblePages = useMemo(() => {
        const range = [];
        const { currentPage, totalPages } = pagination;

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (currentPage > 3) range.push("...");
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                range.push(i);
            }
            if (currentPage < totalPages - 2) range.push("...");
            range.push(totalPages);
        }

        return range;
    }, [pagination]);

    const startItem = (pagination.currentPage - 1) * pagination.limit + 1;
    const endItem = Math.min(pagination.currentPage * pagination.limit, pagination.totalCount);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return Object.values(currentFilters).some((value) => value !== undefined && value !== "");
    }, [currentFilters]);

    if (!user) {
        return null;
    }

    return (
        <>
            <div className="page-title files-title">
                <div className="title database-files-title">{t("files")}</div>
                <div className="files-title-tools">
                    <Tooltip
                        title={isRefreshing ? t("refreshing") || "Refreshing..." : t("refreshFiles") || "Refresh files"}
                        arrow
                    >
                        <RefreshIcon
                            className={`refresh-files-icon ${isRefreshing ? "refresh-files-icon-spinning" : ""}`}
                            onClick={handleManualRefresh}
                        />
                    </Tooltip>
                </div>
                <Tooltip
                    title={
                        isFileLimitReached
                            ? t("filesLimitReached")
                            : isRefreshing || isDeleting
                              ? t("pleaseWait") || "Please wait..."
                              : t("uploadFile")
                    }
                    arrow
                >
                    <span className="add-file-wrap">
                        <button
                            type="button"
                            className="btn btn-primary add-file-btn"
                            onClick={openFileDialog}
                            disabled={isFileLimitReached || isDeleting}
                        >
                            <Icon name="ic-plus" />
                            {t("uploadFile")}
                        </button>
                    </span>
                </Tooltip>
            </div>

            {/* Search and Filter Bar */}
            <div className="database-search-filter-bar">
                <div className="database-search-section">
                    <div className="database-search-input-wrapper">
                        <TextInput
                            value={searchQuery}
                            customClassName="database-vector-search-input"
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t("searchFilesVectorQuery") || "Search files by content..."}
                        />
                        <div className="database-search-clear-space">
                            {searchQuery && (
                                <Tooltip title={t("clearSearch") || "Clear search"} arrow>
                                    <CloseCircleIcon
                                        className="database-search-clear-icon"
                                        onClick={() => setSearchQuery("")}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    </div>
                </div>
                <div className="database-filter-actions">
                    <Tooltip title={t("filterFiles") || "Filter files"} arrow>
                        <div className="database-action-button" onClick={() => setIsFilterModalOpen(true)}>
                            <FilterIcon className={`database-action-icon ${hasActiveFilters ? "active-filter" : ""}`} />
                            {hasActiveFilters && <span className="filter-badge"></span>}
                        </div>
                    </Tooltip>
                    <Tooltip
                        title={
                            t("sortByName") +
                            ` (${currentSort.sortBy === "name" ? (currentSort.sortOrder === "asc" ? t("ascending") : t("descending")) : t("notActive")})`
                        }
                        arrow
                    >
                        <div className="database-action-button" onClick={() => handleSortClick("name")}>
                            {currentSort.sortBy === "name" && currentSort.sortOrder === "asc" ? (
                                <SortAscIcon
                                    className={`database-action-icon ${currentSort.sortBy === "name" ? "database-sort-active" : ""}`}
                                />
                            ) : (
                                <SortDescIcon
                                    className={`database-action-icon ${currentSort.sortBy === "name" ? "database-sort-active" : ""}`}
                                />
                            )}
                        </div>
                    </Tooltip>
                    <Tooltip
                        title={
                            t("sortByCreatedAt") +
                            ` (${currentSort.sortBy === "createdAt" ? (currentSort.sortOrder === "asc" ? t("ascending") : t("descending")) : t("notActive")})`
                        }
                        arrow
                    >
                        <div className="database-action-button" onClick={() => handleSortClick("createdAt")}>
                            {currentSort.sortBy === "createdAt" && currentSort.sortOrder === "asc" ? (
                                <SortNumberAscIcon
                                    className={`database-action-icon ${currentSort.sortBy === "createdAt" ? "database-sort-active" : ""}`}
                                />
                            ) : (
                                <SortNumberDescIcon
                                    className={`database-action-icon ${currentSort.sortBy === "createdAt" ? "database-sort-active" : ""}`}
                                />
                            )}
                        </div>
                    </Tooltip>
                </div>
            </div>

            <div {...getRootProps()} className={`folder-content ${isDragActive ? "drag-active" : ""}`}>
                <input {...getInputProps()} />
                {isFileLimitReached && <div className="upload-error-message">{t("filesLimitReached")}</div>}
                {errorMessage && <div className="upload-error-message">{errorMessage}</div>}

                {isUploading && <LoadingSpinner />}
                {!isUploading && files.length > 0 ? (
                    <>
                        <div className="database-files-table-container">
                            <table className="database-files-table">
                                <thead>
                                    <tr>
                                        <th>{t("name")}</th>
                                        {searchQuery && <th>{t("matchType") || "Match Type"}</th>}
                                        <th>{t("folderDB")}</th>
                                        <th>{t("dateAdded")}</th>
                                        <th className="database-file-actions-header"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((file) => (
                                        <tr key={file.id} className="database-file-row">
                                            <td className="database-file-name-cell">
                                                <div className="database-file-name-wrapper">
                                                    {getFileTypeBadge(file.name || "")}
                                                    <div className="file-name-details">
                                                        <span className="file-name">{file.name || "Unnamed File"}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            {searchQuery && (
                                                <td className="database-file-match-type-cell">
                                                    {renderMatchTypeIcons(file._searchMatch?.matchType, t)}
                                                </td>
                                            )}
                                            <td className="database-file-folder-cell">
                                                {file.companyRagFolder?.name || "-"}
                                            </td>
                                            <td className="database-file-date-cell">
                                                {new Date(file.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="database-file-actions-cell">
                                                {file.fileStatus && file.fileStatus !== "completed" ? (
                                                    <Tooltip title={t("processing") || "Processing..."} arrow>
                                                        <TimeIcon className="database-file-action-icon database-file-processing-icon" />
                                                    </Tooltip>
                                                ) : (
                                                    <>
                                                        {file._searchMatch?.snippet && (
                                                            <Tooltip
                                                                title={
                                                                    isDeleting
                                                                        ? t("pleaseWait") || "Please wait..."
                                                                        : t("matchedTextInVectorSearch") ||
                                                                          "View content preview"
                                                                }
                                                                arrow
                                                            >
                                                                <MenuSearchIcon
                                                                    className={`database-file-action-icon database-file-snippet-icon ${isDeleting ? "disabled" : ""}`}
                                                                    onClick={() =>
                                                                        !isDeleting && handleShowSnippet(file)
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        {file.url && (
                                                            <>
                                                                <Tooltip
                                                                    title={
                                                                        isDeleting
                                                                            ? t("pleaseWait") || "Please wait..."
                                                                            : t("preview")
                                                                    }
                                                                    arrow
                                                                >
                                                                    <EyeIcon
                                                                        className={`database-file-action-icon database-file-preview-icon ${isDeleting ? "disabled" : ""}`}
                                                                        onClick={() =>
                                                                            !isDeleting && handlePreviewFile(file)
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip
                                                                    title={
                                                                        isDeleting
                                                                            ? t("pleaseWait") || "Please wait..."
                                                                            : t("download")
                                                                    }
                                                                    arrow
                                                                >
                                                                    <DownloadIcon
                                                                        className={`database-file-action-icon database-file-download-icon ${isDeleting ? "disabled" : ""}`}
                                                                        onClick={() =>
                                                                            !isDeleting &&
                                                                            handleFileDownload(file.url, file.name)
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        <Tooltip
                                                            title={
                                                                isDeleting
                                                                    ? t("pleaseWait") || "Please wait..."
                                                                    : t("editMetadata")
                                                            }
                                                            arrow
                                                        >
                                                            <SettingsIcon
                                                                className={`database-file-action-icon database-file-edit-icon ${isDeleting ? "disabled" : ""}`}
                                                                onClick={() => !isDeleting && handleEditMetadata(file)}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={
                                                                isDeleting
                                                                    ? t("pleaseWait") || "Please wait..."
                                                                    : t("delete")
                                                            }
                                                            arrow
                                                        >
                                                            <DeleteBinSvg
                                                                className={`database-file-action-icon database-file-delete-icon ${isDeleting ? "disabled" : ""}`}
                                                                onClick={() =>
                                                                    !isDeleting && handleOpenDeleteModal(file)
                                                                }
                                                            />
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!searchQuery && pagination.totalPages > 1 && (
                            <div className="pagination-controls">
                                <button
                                    disabled={!pagination.hasPreviousPage}
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                >
                                    ‹
                                </button>

                                {visiblePages.map((p, idx) =>
                                    p === "..." ? (
                                        <span key={`ellipsis-${idx}`} className="ellipsis">
                                            …
                                        </span>
                                    ) : (
                                        <button
                                            key={p}
                                            className={p === pagination.currentPage ? "active" : ""}
                                            onClick={() => handlePageChange(p as number)}
                                        >
                                            {p}
                                        </button>
                                    ),
                                )}

                                <button
                                    disabled={!pagination.hasNextPage}
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                >
                                    ›
                                </button>

                                <span className="pagination-total">
                                    {t("displayedFiles") || "Displayed files"}:{" "}
                                    {`${startItem}–${endItem} / ${pagination.totalCount}`}
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="no-files-upload-zone">
                        <p className="no-files-message">
                            {searchQuery || hasActiveFilters
                                ? t("noFilesMatchingFilters") || "No files match your search or filters"
                                : t("noFilesInFolderYet") || "No files yet"}
                        </p>
                    </div>
                )}
            </div>

            {/* Upload Metadata Modal */}
            <FileMetadataModal
                isOpen={isMetadataModalOpen}
                onClose={() => {
                    setIsMetadataModalOpen(false);
                    setPendingFiles([]);
                }}
                files={pendingFiles}
                onUpload={handleUploadWithMetadata}
                folderId={selectedFolderId}
            />

            {/* Edit Metadata Modal */}
            {editingFile && (
                <EditFileMetadataModal
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setEditingFile(null);
                    }}
                    fileId={editingFile.id}
                    fileName={editingFile.name}
                    currentMetadata={editingFile.metadata}
                    onUpdate={handleMetadataUpdate}
                    onSave={handleSaveMetadata}
                    isLoading={loading}
                />
            )}

            {/* Filter Modal */}
            <DatabaseFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                currentFilters={currentFilters}
                onApply={handleApplyFilters}
            />

            {/* Snippet Modal */}
            <ChunksModal
                isOpen={isSnippetModalOpen}
                onClose={() => setIsSnippetModalOpen(false)}
                fileName={selectedSnippet.fileName}
                snippet={selectedSnippet.snippet}
                title={selectedSnippet.title}
                description={selectedSnippet.description}
                author={selectedSnippet.author}
            />

            {/* File Preview Modal */}
            {previewFile && (
                <FilePreviewModal
                    isOpen={isPreviewModalOpen}
                    onClose={() => setIsPreviewModalOpen(false)}
                    fileUrl={previewFile.url}
                    fileName={previewFile.name}
                    fileType={previewFile.type}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setFileToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                title={t("deleteFile")}
                message={t("confirmDeleteFile") || `Are you sure you want to delete "${fileToDelete?.name}"?`}
                requireTextConfirmation={true}
            />
        </>
    );
};

export default DatabaseContent;
